import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { useChatMessages, detectContactSharing, type ChatMessage, type MatchInfo } from '@/hooks/useChatMessages';

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: true });
  if (isToday) return time;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `어제 ${time}`;
  return `${d.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })} ${time}`;
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function DateSeparator({ date }: { date: string }) {
  const d = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  let label: string;
  if (d.toDateString() === today.toDateString()) {
    label = '오늘';
  } else if (d.toDateString() === yesterday.toDateString()) {
    label = '어제';
  } else {
    label = d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  return (
    <div className="flex items-center justify-center my-4">
      <span className="text-[10px] text-gray-400 bg-white px-3 py-1 rounded-full border border-gray-100">
        {label}
      </span>
    </div>
  );
}

export default function AppChatPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get('match_id');
  const auth = useAppAuth(true);
  const { unreadCount } = useNotifications();
  const { messages, partner, matchInfo, loading, error, sending, sendMessage } = useChatMessages(matchId || undefined, auth.userId);

  const [input, setInput] = useState('');
  const [contactWarning, setContactWarning] = useState('');
  const [sentWarning, setSentWarning] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 자동 스크롤
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 경고 자동 제거
  useEffect(() => {
    if (contactWarning) {
      const timer = setTimeout(() => setContactWarning(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [contactWarning]);

  useEffect(() => {
    if (sentWarning) {
      const timer = setTimeout(() => setSentWarning(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [sentWarning]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    // 연락처 공유 감지
    const contactCheck = detectContactSharing(trimmed);
    if (contactCheck.detected) {
      setContactWarning(`연락처/메신저 정보('${contactCheck.pattern}')가 감지되었습니다. 플랫폼 내 채팅을 이용해주세요.`);
      return;
    }

    const success = await sendMessage(trimmed);
    if (success) {
      setInput('');
      setSentWarning('');
    } else {
      setSentWarning('메시지 전송에 실패했습니다. 다시 시도해주세요.');
    }
  }

  // 메시지 그룹화 (날짜별 + 연속 발신자별)
  function groupMessages(msgs: ChatMessage[]) {
    if (msgs.length === 0) return [];

    const groups: { date: string; messages: ChatMessage[]; isMine: boolean }[] = [];
    let currentDate = '';
    let currentIsMine: boolean | null = null;

    for (const msg of msgs) {
      const msgDate = new Date(msg.created_at).toDateString();
      const isMine = msg.sender_id === auth.userId;

      if (msgDate !== currentDate || isMine !== currentIsMine) {
        currentDate = msgDate;
        currentIsMine = isMine;
        groups.push({ date: msgDate, messages: [msg], isMine });
      } else {
        groups[groups.length - 1].messages.push(msg);
      }
    }
    return groups;
  }

  const grouped = groupMessages(messages);
  let lastDateShown = '';

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex flex-col h-screen bg-[#F8F9FA]">
        {/* 헤더 */}
        <header className="px-4 pt-3 bg-white border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3 pb-2.5">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors shrink-0"
            >
              <i className="ri-arrow-left-line text-gray-600" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-extrabold text-gray-900 truncate">
                  {partner?.name || '로딩중...'}
                </h1>
                {matchInfo?.status === 'accepted' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 font-bold shrink-0 whitespace-nowrap">진행중</span>
                )}
                {matchInfo?.status === 'completed' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-bold shrink-0 whitespace-nowrap">완료</span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {partner?.role === 'caregiver' ? '간병인' : '환자/보호자'}
              </p>
            </div>
            <button
              onClick={() => navigate('/app/notifications')}
              className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors relative shrink-0"
            >
              <i className="ri-notification-3-line text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* 매칭 정보 바 */}
          {matchInfo && (
            <div className="flex items-center gap-2 pb-2.5 border-t border-gray-50 pt-2.5 overflow-x-auto scrollbar-none">
              {matchInfo.match_number && (
                <span className="text-[10px] text-gray-500 bg-gray-50 px-2 py-1 rounded-md font-medium shrink-0 flex items-center gap-1">
                  <i className="ri-hashtag text-gray-300 text-xs" />
                  {matchInfo.match_number}
                </span>
              )}
              {(matchInfo.start_date || matchInfo.end_date) && (
                <span className="text-[10px] text-gray-500 bg-gray-50 px-2 py-1 rounded-md font-medium shrink-0 flex items-center gap-1">
                  <i className="ri-calendar-line text-gray-300 text-xs" />
                  {formatShortDate(matchInfo.start_date)} ~ {formatShortDate(matchInfo.end_date)}
                </span>
              )}
              {matchInfo.location && (
                <span className="text-[10px] text-gray-500 bg-gray-50 px-2 py-1 rounded-md font-medium shrink-0 flex items-center gap-1">
                  <i className="ri-map-pin-line text-gray-300 text-xs" />
                  {matchInfo.location.length > 10 ? matchInfo.location.slice(0, 10) + '...' : matchInfo.location}
                </span>
              )}
              {matchInfo.hourly_rate && (
                <span className="text-[10px] text-gray-500 bg-gray-50 px-2 py-1 rounded-md font-medium shrink-0 flex items-center gap-1">
                  <i className="ri-money-dollar-circle-line text-gray-300 text-xs" />
                  {matchInfo.hourly_rate.toLocaleString()}원
                </span>
              )}
            </div>
          )}
        </header>

        {/* 에러 상태 */}
        {error ? (
          <div className="flex-1 flex items-center justify-center px-5">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-2xl text-red-400" />
              </div>
              <p className="text-sm text-gray-700 font-bold mb-1">채팅을 불러올 수 없습니다</p>
              <p className="text-xs text-gray-400 mb-4">{error}</p>
              <button
                onClick={() => navigate('/app/matching')}
                className="px-5 py-2.5 rounded-xl text-xs font-bold text-white cursor-pointer hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#2E7D6B' }}
              >
                매칭 페이지로 이동
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 메시지 목록 */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-chat-3-line text-2xl text-gray-300" />
                    </div>
                    <p className="text-sm text-gray-500 font-medium mb-1">대화를 시작해보세요</p>
                    <p className="text-xs text-gray-400">
                      {partner?.name || '상대방'}님과의 첫 메시지를 보내보세요.
                    </p>
                    {partner?.phone && (
                      <p className="text-xs text-gray-300 mt-3">
                        <i className="ri-phone-line mr-1" />
                        {partner.phone}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {grouped.map((group, gi) => {
                    const showDate = group.date !== lastDateShown;
                    lastDateShown = group.date;

                    return (
                      <div key={gi}>
                        {showDate && <DateSeparator date={group.date} />}
                        <div className={`flex ${group.isMine ? 'justify-end' : 'justify-start'} mb-1`}>
                          <div className={`max-w-[75%] ${group.isMine ? 'items-end' : 'items-start'} flex flex-col`}>
                            {group.messages.map((msg, mi) => (
                              <div
                                key={msg.id}
                                className={`px-3.5 py-2 text-sm leading-relaxed ${
                                  group.isMine
                                    ? 'bg-[#2E7D6B] text-white'
                                    : 'bg-white text-gray-900 border border-gray-100'
                                } ${
                                  group.messages.length === 1
                                    ? 'rounded-2xl'
                                    : mi === 0
                                      ? group.isMine
                                        ? 'rounded-t-2xl rounded-l-2xl rounded-br-md'
                                        : 'rounded-t-2xl rounded-r-2xl rounded-bl-md'
                                      : mi === group.messages.length - 1
                                        ? group.isMine
                                          ? 'rounded-b-2xl rounded-l-2xl rounded-tr-md'
                                          : 'rounded-b-2xl rounded-r-2xl rounded-tl-md'
                                        : group.isMine
                                          ? 'rounded-l-2xl rounded-r-md'
                                          : 'rounded-r-2xl rounded-l-md'
                                }`}
                                style={{
                                  wordBreak: 'break-word',
                                }}
                              >
                                {msg.content}
                              </div>
                            ))}
                            <span className={`text-[10px] text-gray-400 mt-0.5 ${group.isMine ? 'text-right' : 'text-left'} px-1`}>
                              {formatTime(group.messages[group.messages.length - 1].created_at)}
                              {group.isMine && group.messages.every((m) => m.is_read) && (
                                <span className="ml-1 text-[#2E7D6B]">읽음</span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* 연락처 공유 경고 */}
            {contactWarning && (
              <div className="px-4 pb-1 shrink-0">
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500 text-sm shrink-0" />
                  <p className="text-xs text-red-600 font-medium">{contactWarning}</p>
                </div>
              </div>
            )}

            {/* 전송 실패 경고 */}
            {sentWarning && (
              <div className="px-4 pb-1 shrink-0">
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
                  <i className="ri-error-warning-line text-amber-500 text-sm shrink-0" />
                  <p className="text-xs text-amber-600 font-medium">{sentWarning}</p>
                </div>
              </div>
            )}

            {/* 입력창 */}
            <form
              onSubmit={handleSend}
              className="px-4 py-3 bg-white border-t border-gray-100 shrink-0"
              style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
            >
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/app/matching')}
                  className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors shrink-0"
                  title="매칭 정보"
                >
                  <i className="ri-information-line text-gray-500 text-sm" />
                </button>
                <div className="flex-1 relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      if (contactWarning) setContactWarning('');
                    }}
                    placeholder="메시지를 입력하세요"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 bg-gray-50 focus:outline-none focus:border-[#2E7D6B] focus:bg-white transition-colors"
                    maxLength={1000}
                    disabled={sending}
                  />
                </div>
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: input.trim() && !sending ? '#2E7D6B' : '#E0E0E0' }}
                >
                  {sending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <i className="ri-send-plane-fill text-white text-lg" />
                  )}
                </button>
              </div>
              <p className="text-[9px] text-gray-400 mt-1.5 text-center">
                개인 연락처 공유는 제한됩니다. 플랫폼 내에서 안전하게 대화하세요.
              </p>
            </form>
          </>
        )}
      </div>
    </AppLayout>
  );
}